import React, { useEffect, useMemo, useState } from 'react'
import { Pagination } from 'rsuite'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { trans } from 'sharedKernel/i18n'
import { Filter } from 'components/Filter'
import { Loading } from 'components/loading'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { TeiaNewStartupCard } from 'TEIA/components/NewStartupCard'
import {
  ScoutingStartup,
  useGetConnectionFunnel,
} from 'domain/feature/hub/company/scouting/api/scoutingService'
import {
  getConnectionFunnelStartups,
  normalizeScoutingStartupRow,
} from '../../normalizeScoutingStartupRow'
import { SearchFilterContainer, SearchWrapper } from '../ScoutingSelectStartup.styles'
import {
  EmptyCard,
  EmptyIcon,
  EmptyIconWrapper,
  EmptyTitle,
  EmptyHint,
  ItemsPerPageLabel,
  PaginationChevronIcon,
  PaginationControls,
  PaginationFooter,
  PaginationNextButton,
  PaginationPrevButton,
  StartupsList,
} from './styles'

interface ScoutingInterestedStartupsProps {
  opportunityId?: string
}

const PAGE_SIZE = 10

export const ScoutingInterestedStartups = ({
  opportunityId,
}: ScoutingInterestedStartupsProps): JSX.Element => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const { data: funnelResponse, isLoading } = useGetConnectionFunnel(opportunityId ?? '')

  const interestedStartups = useMemo<ScoutingStartup[]>(() => {
    const rows = getConnectionFunnelStartups(funnelResponse)
    return rows.map((startup: Record<string, unknown>) => normalizeScoutingStartupRow(startup))
  }, [funnelResponse])

  const filteredStartups = useMemo(() => {
    if (!searchTerm.trim()) return interestedStartups

    const term = searchTerm.toLowerCase()
    return interestedStartups.filter(startup =>
      startup.name?.toLowerCase().includes(term) ||
      startup.description?.toLowerCase().includes(term) ||
      (startup as ScoutingStartup & { category?: string }).category?.toLowerCase().includes(term)
    )
  }, [interestedStartups, searchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const totalRows = filteredStartups.length
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE))

  const paginatedStartups = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE
    return filteredStartups.slice(startIndex, startIndex + PAGE_SIZE)
  }, [filteredStartups, currentPage])

  const handleSearch = (term: string): void => {
    setSearchTerm(term)
  }

  const handlePageChange = (eventKey: string | number): void => {
    setCurrentPage(Number(eventKey))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleOpenStartupProfile = (startup: ScoutingStartup): void => {
    navigate(`/hub/company/scouting/startup/${startup.id}`, { state: { startup } })
  }

  if (!opportunityId) {
    return (
      <EmptyCard>
        <EmptyIconWrapper>
          <EmptyIcon>
            <FontAwesomeIcon icon={['fas', 'tower-broadcast']} />
          </EmptyIcon>
        </EmptyIconWrapper>
        <EmptyTitle>{trans('scouting.interested.missingIds')}</EmptyTitle>
      </EmptyCard>
    )
  }

  if (isLoading && !funnelResponse) {
    return <Loading />
  }

  const hasData = interestedStartups.length > 0
  const hasFilteredResults = filteredStartups.length > 0

  return (
    <div>
      <SearchFilterContainer>
        <SearchWrapper>
          <Filter
            btnClick={handleSearch}
            currentSearchTerm={searchTerm}
            searchBtn={true}
            minSearchTermLength={0}
            onFilterChange={setSearchTerm}
          />
        </SearchWrapper>
      </SearchFilterContainer>

      {!hasData ? (
        <EmptyCard>
          <EmptyIconWrapper>
            <EmptyIcon>
              <FontAwesomeIcon icon={['fas', 'tower-broadcast']} />
            </EmptyIcon>
          </EmptyIconWrapper>
          <EmptyTitle>{trans('Nenhuma startup interessada no momento')}</EmptyTitle>
          <EmptyHint>
            {trans('Quando uma startup demonstrar interesse nessa oportunidade, ela aparecerá aqui.')}
          </EmptyHint>
        </EmptyCard>
      ) : !hasFilteredResults ? (
        <EmptyCard>
          <EmptyIconWrapper>
            <EmptyIcon>
              <FontAwesomeIcon icon={['fas', 'magnifying-glass']} />
            </EmptyIcon>
          </EmptyIconWrapper>
          <EmptyTitle>{trans('Nenhum resultado encontrado')}</EmptyTitle>
          <EmptyHint>
            {trans('Tente outro termo na busca ou limpe o filtro.')}
          </EmptyHint>
        </EmptyCard>
      ) : (
        <>
          <StartupsList>
            {paginatedStartups.map((startup, index) => (
              <TeiaNewStartupCard
                key={startup.id || `startup-${index}`}
                title={startup.name}
                pitch={startup.description}
                logo={startup.logo}
                activity={(startup as ScoutingStartup & { category?: string }).category}
                businessModel={startup.businessModel}
                target={startup.target}
                numberEmployees={startup.numberEmployees}
                state={startup.addressState}
                onViewProfile={() => handleOpenStartupProfile(startup)}
                hideViewProfileButton
              />
            ))}
          </StartupsList>

          <PaginationFooter>
            <PaginationControls>
              <PaginationPrevButton
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <PaginationChevronIcon className="bi bi-chevron-left" />
              </PaginationPrevButton>
              <Pagination
                activePage={currentPage}
                pages={totalPages}
                maxButtons={3}
                ellipsis={true}
                boundaryLinks={true}
                onSelect={handlePageChange}
                size="sm"
                total={totalRows}
                className="custom-pagination"
              />
              <PaginationNextButton
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
              >
                <PaginationChevronIcon className="bi bi-chevron-right" />
              </PaginationNextButton>
            </PaginationControls>
            <ItemsPerPageLabel className="items-per-page">
              {PAGE_SIZE} {trans('scouting.opportunity.list.itemsPerPage')}
            </ItemsPerPageLabel>
          </PaginationFooter>
        </>
      )}
    </div>
  )
}
